import { z } from 'zod'
import type { NotificationType } from './notifications'
import type { Language } from './simulation'

/**
 * 광고성 정보 수신 동의 (로드맵 P6, 정보통신망법 제50조, 2026-10-27).
 * 광고성 푸시는 따로 미리 동의한 사람에게만, 거래성 푸시는 지금처럼 보낸다.
 * 필수 동의로는 받을 수 없어서(제50조·개인정보보호법 제22조) 새 앱은 '동의'와 '동의 안 함' 중 하나를 꼭 고르게 하고,
 * 어느 쪽을 골라도 앱 기능은 그대로다. 기록이 없으면(옛 앱·아직 고르지 않음) 미동의로 본다.
 */

/** 푸시 한 건의 성격. advertising = 영리 목적의 광고성 정보(동의·(광고) 표기·야간 제한), transactional = 거래·이용 관계 안내 */
export type PushPurpose = 'advertising' | 'transactional'

/**
 * 서버가 보내는 푸시의 종류. 인앱 알림 종류(NotificationType)에 손님 푸시와 관리자 발송을 나눠 더했다.
 * 관리자 발송은 인앱 알림 종류가 모두 'welcome' 이라 성격으로 갈라 둔다
 */
export type PushKind =
  | Exclude<NotificationType, 'welcome'>
  | 'admin_message'
  | 'admin_broadcast_notice'
  | 'admin_broadcast_ad'
  | 'guest_sim_done'
  | 'guest_reminder'

/**
 * 푸시 종류별 광고성/거래성 분류 — 이 표 하나로 정한다.
 * 기준(KISA 「불법스팸 방지를 위한 정보통신망법 안내서」): 이용자가 요청한 거래·이용의 진행·결과 안내는 거래성,
 * 그 안에 유료 전환·가입 권유가 들어가면 광고성이다. 애매하면 광고성으로 둔다(보수적으로)
 */
export const PUSH_PURPOSE: Record<PushKind, PushPurpose> = {
  /** 이용자가 요청한 제품 생성의 결과 */
  sim_done: 'transactional',
  sim_failed: 'transactional',
  /** 이용자가 결제하고 보낸 견적 요청의 접수·도착·종료 */
  quote_submitted: 'transactional',
  quote_quoted: 'transactional',
  quote_closed: 'transactional',
  /** 이용자가 쓴 글에 달린 댓글·투표. 영리 권유 없음 */
  post_comment: 'transactional',
  comment_reply: 'transactional',
  post_vote: 'transactional',
  /** 결제·구독 상태가 바뀌었다는 확인 (planChanged·planEnded 는 업그레이드를 권하지 않는다) */
  plan_changed: 'transactional',
  /** '플랜을 올리면 더 만들 수 있어요' + 페이월로 이동 — 유료 전환 권유 */
  plan_limit: 'advertising',
  /** 남은 생성 수 안내지만 누르면 페이월로 간다 — 유료 전환 권유 */
  quota_warning: 'advertising',
  /** 운영자가 한 사람에게 보내는 안내(문의 답변·계정 조치). 광고는 여기로 보내지 않고 공지의 '광고성'으로 보낸다 */
  admin_message: 'transactional',
  /** 약관 변경·장애처럼 광고가 없는 전체 공지 */
  admin_broadcast_notice: 'transactional',
  /** 새 기능·혜택·이벤트 홍보 전체 공지 */
  admin_broadcast_ad: 'advertising',
  /** 손님이 요청한 생성의 결과 */
  guest_sim_done: 'transactional',
  /** '계정에 저장하지 않으면 사라져요' — 회원 가입 유도 리마인더 (로드맵 P6) */
  guest_reminder: 'advertising',
  /** 무료 한도에 막힌 뒤 '플랜을 올리면 더 만들 수 있어요' 재방문 알림 — 유료 전환 권유 */
  plan_limit_reminder: 'advertising',
  /** 완성한 제품에 '공장 견적을 받아 보세요' 재방문 알림 — 유료 견적 구매 권유 */
  quote_nudge: 'advertising',
  /** 이용자가 결제하고 요청한 전문가 검수의 완료·반려 결과 */
  expert_review: 'transactional',
  /**
   * 유료 구독자의 '남은 생성 N회가 3일 뒤 초기화돼요(이월 없음)' 안내 (2026-10-30). 이미 결제한 이용권의 소멸 예정을 알리는
   * 사실 안내라 포인트·마일리지 소멸 안내처럼 거래성으로 본다. 그래서 업그레이드·구매 권유 문구를 넣지 않고 페이월이 아니라 홈으로 보낸다.
   * 문구에 권유가 들어가면 광고성으로 바꾼다 (이 줄 하나)
   */
  generation_reset_reminder: 'transactional',
  /** 이용자가 결제한 샘플 주문의 결제 확인·공장 진행 */
  sample_update: 'transactional',
  /** 이용자가 결제한 샘플 주문에 공장 메시지가 왔다는 안내 */
  sample_message: 'transactional',
}

/** 인앱 알림 종류의 기본 푸시 종류. 'welcome' 은 관리자 1:1 안내로 본다(전체 공지는 호출 쪽이 따로 정한다) */
export function pushKindOfNotification(type: NotificationType): PushKind {
  return type === 'welcome' ? 'admin_message' : type
}

/** 관리자 전체 공지의 성격. 기본은 광고성 — 고르지 않으면 동의한 사람에게만 간다 */
export const BROADCAST_PURPOSES = ['advertising', 'notice'] as const
export type BroadcastPurpose = (typeof BROADCAST_PURPOSES)[number]

/**
 * 동의 뒤 2년이 지나면 다시 묻는다(정보통신망법 시행령 제62조의3: 2년마다 수신동의 여부 확인).
 * 확인 안내를 따로 보내는 대신, 지나면 미동의처럼 보내지 않고 앱이 선택 화면을 다시 띄운다
 */
export const MARKETING_CONSENT_VALID_DAYS = 730

/** unknown = 고른 적 없음(옛 앱 포함), expired = 동의한 지 2년이 지나 다시 물어야 함 */
export type MarketingConsentStatus = 'unknown' | 'granted' | 'denied' | 'expired'

export interface MarketingConsentRecord {
  granted: boolean
  consentedAt: Date | null
}

/** 저장된 기록의 지금 상태. 서버(발송 판단·응답)가 쓴다 */
export function marketingConsentStatus(
  record: MarketingConsentRecord | null | undefined,
  now: Date,
): MarketingConsentStatus {
  if (!record) return 'unknown'
  if (!record.granted) return 'denied'
  if (!record.consentedAt) return 'unknown'
  const ageMs = now.getTime() - record.consentedAt.getTime()
  if (!Number.isFinite(ageMs) || ageMs < 0) return 'unknown'
  return ageMs >= MARKETING_CONSENT_VALID_DAYS * 86_400_000 ? 'expired' : 'granted'
}

/** GET·PUT /me/marketing-consent 응답 */
export interface MarketingConsentView {
  status: MarketingConsentStatus
  /** 마지막으로 고른(동의·거부·철회) 시각 — 처리 결과 안내에 쓴다 */
  decidedAt: string | null
}

/** PUT /me/marketing-consent — 동의·거부·철회 */
export const marketingConsentUpdateSchema = z.object({
  granted: z.boolean(),
  /** prompt = 처음 묻는 선택 화면, settings = 계정 탭 토글 */
  source: z.enum(['prompt', 'settings']),
  /** 기기 시간대(IANA, 예: Asia/Seoul). 한국 시간의 제한과 함께 이 시간대의 야간도 제한한다. 없거나 모르는 값이면 KST */
  timeZone: z.string().max(64).optional(),
})
export type MarketingConsentUpdate = z.infer<typeof marketingConsentUpdateSchema>

/**
 * 앱 계정 탭의 수신 토글 이름. 광고 푸시 끝의 수신 거부 안내(API lib/marketing-consent.ts)가 이 이름으로 길을 알려 주므로
 * 앱 화면과 푸시가 같은 값을 쓴다
 */
export const MARKETING_CONSENT_TOGGLE_LABEL: Record<Language, string> = {
  ko: '혜택·이벤트 알림 받기',
  en: 'Get offers & event alerts',
  ja: 'お得・イベント通知を受け取る',
  zh: '接收优惠与活动通知',
}
